import {
    ConnectedPosition,
    Overlay,
    OverlayPositionBuilder,
    OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FieldTranslation, PopupDirection } from '../../models';
import { FlooriTooltipComponent } from '../../components';

@Directive({
    selector: '[flooriTooltip]',
    standalone: true,
})
export class TooltipDirective implements OnDestroy {
    private tooltipText = new BehaviorSubject<string | FieldTranslation>('');
    private subscription: Subscription | null = null;
    @Input() isMobile = false;
    @Input() isMobileBySize = false;
    @Input() tooltipDirection?: PopupDirection;
    @Input() tooltipWidth?: number;
    @Input() tooltipOnMobile = false;
    @Input() disableArrow = false;
    @Input() disableTranslation = false;
    @Input() offset = 16;

    private overlayRef: OverlayRef | null = null;

    @Input()
    set flooriTooltip(value: string | FieldTranslation) {
        this.tooltipText.next(value);
    }

    constructor(
        private readonly overlay: Overlay,
        private readonly elementRef: ElementRef,
        private readonly positionBuilder: OverlayPositionBuilder,
    ) {}

    @HostListener('mouseenter') show(): void {
        if (!this.tooltipOnMobile && (this.isMobile || this.isMobileBySize)) {
            return;
        }

        if (!this.tooltipText.value) {
            return;
        }

        this.hide();

        const positionStrategy = this.positionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions([this.getPosition()]);

        this.overlayRef = this.overlay.create({ positionStrategy });

        const tooltipPortal = new ComponentPortal(FlooriTooltipComponent);
        const tooltipRef = this.overlayRef.attach(tooltipPortal);

        tooltipRef.instance.text = this.tooltipText.value;
        tooltipRef.instance.width = this.tooltipWidth;
        tooltipRef.instance.direction = this.tooltipDirection;
        tooltipRef.instance.disableArrow = this.disableArrow;
        tooltipRef.instance.disableTranslation = this.disableTranslation;

        this.subscription = this.tooltipText.subscribe(value => {
            if (tooltipRef.instance) {
                tooltipRef.instance.text = value;
                tooltipRef.instance.cdr.markForCheck();
            }
        });
    }

    @HostListener('mouseleave') hide(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    ngOnDestroy(): void {
        this.hide();
    }

    private getPosition(): ConnectedPosition {
        const offset = this.offset;

        switch (this.tooltipDirection) {
            case PopupDirection.above:
                return {
                    originX: 'center',
                    originY: 'top',
                    overlayX: 'center',
                    overlayY: 'bottom',
                    offsetY: -offset,
                };
            case PopupDirection.below:
                return {
                    originX: 'center',
                    originY: 'bottom',
                    overlayX: 'center',
                    overlayY: 'top',
                    offsetY: offset,
                };
            case PopupDirection.left:
                return {
                    originX: 'start',
                    originY: 'center',
                    overlayX: 'end',
                    overlayY: 'center',
                    offsetX: -offset,
                };
            case PopupDirection.right:
                return {
                    originX: 'end',
                    originY: 'center',
                    overlayX: 'start',
                    overlayY: 'center',
                    offsetX: offset,
                };
            default:
                return {
                    originX: 'center',
                    originY: 'bottom',
                    overlayX: 'center',
                    overlayY: 'top',
                };
        }
    }
}
